import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { ArrowRight, Lock, Shield, Users, TrendingUp, Zap, Globe, DollarSign, Target, Eye, EyeOff, Coins, Calendar, Gift } from 'lucide-react';

// Access code for the pitch deck
const PITCH_ACCESS_CODE = "INVESTOR2024";

const PitchDeck = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleAccessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    setTimeout(() => {
      if (accessCode === PITCH_ACCESS_CODE) {
        setIsAuthenticated(true);
        toast.success("Access granted to investor pitch deck");
      } else {
        toast.error("Invalid access code");
      }
      setIsLoading(false);
    }, 500);
  };

  const slides = [
    {
      title: "WhirlPool",
      subtitle: "Private Transactions on Solana",
      content: (
        <div className="text-center space-y-6">
          <div className="flex justify-center items-center w-20 h-20 mx-auto">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="url(#paint0_linear)" strokeWidth="2" />
              <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke="url(#paint1_linear)" strokeWidth="2" />
              <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" fill="url(#paint2_linear)" />
              <defs>
                <linearGradient id="paint0_linear" x1="2" y1="12" x2="22" y2="12" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#22D3EE" />
                  <stop offset="1" stopColor="#0EA5E9" />
                </linearGradient>
                <linearGradient id="paint1_linear" x1="6" y1="12" x2="18" y2="12" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#22D3EE" />
                  <stop offset="1" stopColor="#0EA5E9" />
                </linearGradient>
                <linearGradient id="paint2_linear" x1="10" y1="12" x2="14" y2="12" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#22D3EE" />
                  <stop offset="1" stopColor="#0EA5E9" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <p className="text-xl text-whirlpool-light">
            Secure, anonymous transactions on the Solana blockchain using advanced cryptographic protocols
          </p>
          <div className="flex justify-center gap-4">
            <Badge variant="secondary" className="bg-whirlpool-blue/20 text-whirlpool-cyan">Privacy First</Badge>
            <Badge variant="secondary" className="bg-whirlpool-blue/20 text-whirlpool-cyan">Solana Native</Badge>
            <Badge variant="secondary" className="bg-whirlpool-blue/20 text-whirlpool-cyan">Lightning Fast</Badge>
          </div>
        </div>
      )
    },
    {
      title: "The Problem",
      subtitle: "Blockchain Transparency vs Privacy",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-red-500/10 border-red-500/20">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Public Ledger
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">All transactions are visible on-chain, compromising user privacy and enabling transaction tracking</p>
              </CardContent>
            </Card>
            <Card className="bg-red-500/10 border-red-500/20">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  MEV Exploitation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">Front-running and sandwich attacks extract value from user transactions</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center">
            <p className="text-lg text-whirlpool-light">
              <strong>$1.2B+</strong> extracted through MEV in 2023 alone
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Users need privacy protection without sacrificing speed or cost
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Our Solution",
      subtitle: "Privacy-Preserving Technology Stack",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Tornado Protocol
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">Zero-knowledge proofs ensure transaction privacy while maintaining verifiability</p>
              </CardContent>
            </Card>
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Solana Speed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">Sub-second finality with fees under $0.01 per transaction</p>
              </CardContent>
            </Card>
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Multi-Destination
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">Split transactions across multiple recipients in a single private operation</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center">
            <p className="text-lg text-whirlpool-light">
              The first production-ready privacy solution for Solana
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Market Opportunity",
      subtitle: "Massive Growth in Privacy-Conscious Users",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan">Total Addressable Market</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-3xl font-bold text-white">$2.1T</div>
                <p className="text-sm text-whirlpool-light">Total crypto market cap with growing privacy demands</p>
              </CardContent>
            </Card>
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan">Solana Ecosystem</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-3xl font-bold text-white">$8.2B</div>
                <p className="text-sm text-whirlpool-light">Total Value Locked with 400% YoY growth</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center">
            <div className="flex justify-center items-center gap-8">
              <div>
                <div className="text-2xl font-bold text-whirlpool-cyan">85%</div>
                <p className="text-sm text-muted-foreground">Users want privacy</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-whirlpool-cyan">12M+</div>
                <p className="text-sm text-muted-foreground">Solana monthly users</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-whirlpool-cyan">1.7%</div>
                <p className="text-sm text-muted-foreground">Revenue share target</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Token Mechanics",
      subtitle: "Advanced Tokenomics & Holder Benefits",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Token Buybacks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-2xl font-bold text-white">0.2%</div>
                <p className="text-sm text-whirlpool-light">Of transaction fees used for monthly token buybacks</p>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">• Reduces circulating supply</p>
                  <p className="text-xs text-muted-foreground">• Creates deflationary pressure</p>
                  <p className="text-xs text-muted-foreground">• Executed monthly via DEX</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Presale Locks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-2xl font-bold text-white">80%</div>
                <p className="text-sm text-whirlpool-light">Of presale tokens locked for 4 quarters</p>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">• 20% released each quarter</p>
                  <p className="text-xs text-muted-foreground">• Prevents early dumping</p>
                  <p className="text-xs text-muted-foreground">• Ensures long-term commitment</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <Gift className="h-5 w-5" />
                  Holder Rewards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-2xl font-bold text-white">0.5%</div>
                <p className="text-sm text-whirlpool-light">Of fees distributed to top 20 holders monthly</p>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">• Based on token holdings</p>
                  <p className="text-xs text-muted-foreground">• Monthly distribution</p>
                  <p className="text-xs text-muted-foreground">• Incentivizes accumulation</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <div className="bg-whirlpool-blue/10 border-whirlpool-blue/20 rounded-lg p-6">
              <h3 className="text-lg text-whirlpool-light mb-4 text-center">Vesting Schedule</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center bg-whirlpool-blue/5 p-4 rounded-lg">
                  <div className="text-lg font-semibold text-whirlpool-cyan">Q1</div>
                  <p className="text-sm text-muted-foreground">20% Released</p>
                  <p className="text-xs text-muted-foreground">Initial unlock</p>
                </div>
                <div className="text-center bg-whirlpool-blue/5 p-4 rounded-lg">
                  <div className="text-lg font-semibold text-whirlpool-cyan">Q2</div>
                  <p className="text-sm text-muted-foreground">40% Total</p>
                  <p className="text-xs text-muted-foreground">+20% unlock</p>
                </div>
                <div className="text-center bg-whirlpool-blue/5 p-4 rounded-lg">
                  <div className="text-lg font-semibold text-whirlpool-cyan">Q3</div>
                  <p className="text-sm text-muted-foreground">60% Total</p>
                  <p className="text-xs text-muted-foreground">+20% unlock</p>
                </div>
                <div className="text-center bg-whirlpool-blue/5 p-4 rounded-lg">
                  <div className="text-lg font-semibold text-whirlpool-cyan">Q4</div>
                  <p className="text-sm text-muted-foreground">100% Total</p>
                  <p className="text-xs text-muted-foreground">Fully unlocked</p>
                </div>
              </div>
            </div>

            <div className="bg-whirlpool-blue/10 border-whirlpool-blue/20 rounded-lg p-6">
              <h3 className="text-lg text-whirlpool-light mb-4 text-center">Fee Distribution Breakdown</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-whirlpool-cyan">1.0%</div>
                  <p className="text-sm text-muted-foreground">Project Treasury</p>
                  <p className="text-xs text-whirlpool-light">Development & operations</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-whirlpool-cyan">0.2%</div>
                  <p className="text-sm text-muted-foreground">Token Buybacks</p>
                  <p className="text-xs text-whirlpool-light">Monthly deflationary burns</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-whirlpool-cyan">0.5%</div>
                  <p className="text-sm text-muted-foreground">Holder Rewards</p>
                  <p className="text-xs text-whirlpool-light">Top 20 holder distribution</p>
                </div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-whirlpool-light">
                  <strong>Total: 1.7%</strong> transaction fee structure
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Tokenomics",
      subtitle: "WHIRL Token Distribution & Economics",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <Coins className="h-5 w-5" />
                  Token Supply
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-3xl font-bold text-white">100M</div>
                <p className="text-sm text-whirlpool-light">Total WHIRL tokens (fixed supply)</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Presale Target:</span>
                    <span className="text-sm font-semibold">1K SOL</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Total Raise:</span>
                    <span className="text-sm font-semibold">$180-200K</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan">Token Distribution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm">Seed Investors:</span>
                  <span className="text-sm font-semibold">15% (15M)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Public Presale:</span>
                  <span className="text-sm font-semibold">35% (35M)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Team & Development:</span>
                  <span className="text-sm font-semibold">25% (25M)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Community Rewards:</span>
                  <span className="text-sm font-semibold">15% (15M)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Treasury/Reserve:</span>
                  <span className="text-sm font-semibold">10% (10M)</span>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="text-center space-y-4">
            <p className="text-lg text-whirlpool-light">TGE Schedule</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-whirlpool-blue/5 p-4 rounded-lg">
                <p className="text-sm font-semibold text-whirlpool-cyan">Seed Investors</p>
                <p className="text-xs text-muted-foreground">First TGE - Early access</p>
              </div>
              <div className="bg-whirlpool-blue/5 p-4 rounded-lg">
                <p className="text-sm font-semibold text-whirlpool-cyan">Public Presale</p>
                <p className="text-xs text-muted-foreground">Standard TGE - Full release</p>
              </div>
              <div className="bg-whirlpool-blue/5 p-4 rounded-lg">
                <p className="text-sm font-semibold text-whirlpool-cyan">Liquidity Pool</p>
                <p className="text-xs text-muted-foreground">Post-presale - $50K initial</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Revenue Model",
      subtitle: "Aggressive Growth Projections & Revenue Strategy",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Transaction Fees
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm">1.7% fee on all mixed transactions</p>
                  <p className="text-sm text-muted-foreground">+ 0.005 SOL network fee</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Premium Features
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm">Advanced mixing algorithms</p>
                  <p className="text-sm">Enterprise API access</p>
                  <p className="text-sm">Custom integration support</p>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-6">
            <p className="text-lg text-whirlpool-light text-center">Aggressive Revenue Projections</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-whirlpool-blue/5 border-whirlpool-blue/20">
                <CardHeader>
                  <CardTitle className="text-whirlpool-cyan text-center">Month 6</CardTitle>
                  <div className="text-2xl font-bold text-white text-center">$100K</div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground">• 1,000 Monthly Active Users</p>
                  <p className="text-xs text-muted-foreground">• $6K avg monthly volume/user</p>
                  <p className="text-xs text-muted-foreground">• 1.7% fee = $102K/month</p>
                  <p className="text-xs text-whirlpool-cyan">• Target achieved in 6 months</p>
                </CardContent>
              </Card>
              <Card className="bg-whirlpool-blue/5 border-whirlpool-blue/20">
                <CardHeader>
                  <CardTitle className="text-whirlpool-cyan text-center">Year 2</CardTitle>
                  <div className="text-2xl font-bold text-white text-center">$500K</div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground">• 2,500 Monthly Active Users</p>
                  <p className="text-xs text-muted-foreground">• $12K avg monthly volume/user</p>
                  <p className="text-xs text-muted-foreground">• 1.7% fee = $510K/month</p>
                  <p className="text-xs text-whirlpool-cyan">• Market expansion & adoption</p>
                </CardContent>
              </Card>
              <Card className="bg-whirlpool-blue/5 border-whirlpool-blue/20">
                <CardHeader>
                  <CardTitle className="text-whirlpool-cyan text-center">Year 3</CardTitle>
                  <div className="text-2xl font-bold text-white text-center">$1.2M</div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground">• 5,000 Monthly Active Users</p>
                  <p className="text-xs text-muted-foreground">• $14K avg monthly volume/user</p>
                  <p className="text-xs text-muted-foreground">• 1.7% fee = $1.19M/month</p>
                  <p className="text-xs text-whirlpool-cyan">• Enterprise & institutional use</p>
                </CardContent>
              </Card>
            </div>
            <div className="bg-whirlpool-blue/10 border-whirlpool-blue/20 rounded-lg p-4">
              <p className="text-sm text-whirlpool-light text-center mb-2">Growth Strategy</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div>
                  <p className="text-whirlpool-cyan font-semibold">User Acquisition:</p>
                  <p className="text-muted-foreground">• Solana ecosystem partnerships</p>
                  <p className="text-muted-foreground">• DeFi protocol integrations</p>
                  <p className="text-muted-foreground">• Privacy-focused marketing</p>
                </div>
                <div>
                  <p className="text-whirlpool-cyan font-semibold">Volume Growth:</p>
                  <p className="text-muted-foreground">• Institutional clients targeting $50K+/month</p>
                  <p className="text-muted-foreground">• Retail users averaging $1-10K/month</p>
                  <p className="text-muted-foreground">• Premium tiers for high-volume users</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Funding Structure",
      subtitle: "Presale & Seed Investment Strategy",
      content: (
        <div className="space-y-8">
          <div className="text-center space-y-4">
            <div className="text-4xl font-bold text-whirlpool-cyan">$180-200K</div>
            <p className="text-xl text-whirlpool-light">Total Fundraising Target</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan">Seed Round</CardTitle>
                <div className="text-2xl font-bold text-white">$50K</div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">300 SOL allocation</p>
                <p className="text-sm">15% token allocation (15M WHIRL)</p>
                <p className="text-sm text-whirlpool-cyan">✓ First TGE access</p>
                <p className="text-sm text-whirlpool-cyan">✓ Early investor benefits</p>
              </CardContent>
            </Card>
            <Card className="bg-whirlpool-blue/10 border-whirlpool-blue/20">
              <CardHeader>
                <CardTitle className="text-whirlpool-cyan">Public Presale</CardTitle>
                <div className="text-2xl font-bold text-white">$130-150K</div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm">700+ SOL target</p>
                <p className="text-sm">35% token allocation (35M WHIRL)</p>
                <p className="text-sm text-whirlpool-cyan">✓ Open to all participants</p>
                <p className="text-sm text-whirlpool-cyan">✓ Standard TGE schedule</p>
              </CardContent>
            </Card>
          </div>

          <div className="bg-whirlpool-blue/10 border-whirlpool-blue/20 rounded-lg p-6">
            <p className="text-lg text-whirlpool-light mb-4 text-center">Fund Allocation</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-lg font-semibold text-whirlpool-cyan">$100K</div>
                <p className="text-sm text-muted-foreground">Development & Operations</p>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-whirlpool-cyan">$50K</div>
                <p className="text-sm text-muted-foreground">Initial Liquidity Pool</p>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-whirlpool-cyan">$25K</div>
                <p className="text-sm text-muted-foreground">Marketing & Growth</p>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-whirlpool-cyan">$5K</div>
                <p className="text-sm text-muted-foreground">Treasury Buffer</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <p className="text-lg text-whirlpool-light text-center">Key Milestones</p>
            <div className="text-left space-y-2">
              <p className="text-sm">✓ MVP developed and tested</p>
              <p className="text-sm">• Complete seed round ($50K)</p>
              <p className="text-sm">• Launch public presale (1K SOL target)</p>
              <p className="text-sm">• Deploy on Solana mainnet</p>
              <p className="text-sm">• Establish initial liquidity pool</p>
              <p className="text-sm">• Scale to 1K+ users within 6 months</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  if (!isAuthenticated) {
    return (
      <div className="pitch-deck-page min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-whirlpool-blue to-whirlpool-dark p-4">
        <div className="w-full max-w-md bg-black/30 backdrop-blur-md rounded-lg border border-whirlpool-light/20 shadow-xl p-6">
          <div className="flex items-center justify-center mb-6">
            <Lock className="h-8 w-8 text-whirlpool-cyan" />
          </div>
          
          <h1 className="text-2xl font-bold text-center text-white mb-2">Investor Pitch Deck</h1>
          <p className="text-whirlpool-light text-center mb-6">Enter access code to view presentation</p>
          
          <form onSubmit={handleAccessSubmit} className="space-y-4">
            <Input
              type="password"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Access code"
              autoFocus
              className="bg-black/40 border-whirlpool-blue/30 text-white"
              disabled={isLoading}
            />
            
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-whirlpool-blue to-whirlpool-cyan hover:from-whirlpool-cyan hover:to-whirlpool-blue transition-all duration-300" 
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Access Presentation"} 
              {!isLoading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        </div>
        
        <p className="text-whirlpool-light/70 text-sm mt-8">
          For authorized investors only. Contact team for access.
        </p>
      </div>
    );
  }

  return (
    <div className="pitch-deck-page min-h-screen bg-gradient-to-b from-whirlpool-blue to-whirlpool-dark">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="text-sm text-whirlpool-light">
            WhirlPool Investor Presentation
          </div>
          <div className="text-sm text-whirlpool-light">
            {currentSlide + 1} / {slides.length}
          </div>
        </div>

        {/* Slide Content */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-black/20 backdrop-blur-md rounded-lg border border-whirlpool-light/20 p-8 min-h-[600px]">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-white mb-2">{slides[currentSlide].title}</h1>
              <p className="text-xl text-whirlpool-cyan">{slides[currentSlide].subtitle}</p>
            </div>
            
            <div className="flex-1">
              {slides[currentSlide].content}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <Button 
              onClick={prevSlide}
              variant="outline"
              className="border-whirlpool-blue/30 text-whirlpool-light hover:bg-whirlpool-blue/20"
              disabled={currentSlide === 0}
            >
              Previous
            </Button>
            
            <div className="flex gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentSlide 
                      ? 'bg-whirlpool-cyan' 
                      : 'bg-whirlpool-light/30 hover:bg-whirlpool-light/50'
                  }`}
                />
              ))}
            </div>
            
            <Button 
              onClick={nextSlide}
              className="bg-gradient-to-r from-whirlpool-blue to-whirlpool-cyan hover:from-whirlpool-cyan hover:to-whirlpool-blue"
              disabled={currentSlide === slides.length - 1}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchDeck;
